import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { DSSetup, Service } from 'webapp-types';
import { FilterItemType, FilterLogicalOperator, PopupEvent } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import themeClasses from 'webapp/helpers/theme-classes';
import BlockFiltersEditItemComponent from 'webapp/components/block/filters/edit-item';
import BlockFiltersListItemComponent from 'webapp/components/block/filters/list-item';
import FormLeaveConfirmPopupComponent from 'webapp/components/form/leave-confirm/popup';
import FormRadiobuttonGroupComponent from 'webapp/components/form/radiobutton-group';
import FormSelectDimensionsComponent from 'webapp/components/form/select-dimensions';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type { FilterGroup, MetricSetup } from 'webapp/models-custom';
import FilterItem from 'webapp/models-custom/filter/filter-item';
import MetricSetupForMasterFilter from 'webapp/models-custom/metric-setup-for-master-filter';
import type PopupContext from 'webapp/models-custom/popup/context';
import type PopupModal from 'webapp/models-custom/popup/modal';

interface Args {
	popup: PopupModal | PopupContext;
	metricSetup: MetricSetup | MetricSetupForMasterFilter;
	apply: (
		dimensions: string[] | undefined,
		filters: DSSetup.MetricSetupFiltersData | null,
	) => void;
	editItem?: FilterItem;
	theme?: string;
	isInMasterContext?: boolean; // only for master filter in context popup
	allowMultipleDimensions?: boolean; // only for master filter
	onContentChange?: () => void;
	close?: () => void;
	clearAndClose?: () => void;
}

export default class BlockFiltersIndexComponent extends Component<Args> {
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	metricSetup: MetricSetup | MetricSetupForMasterFilter;

	@tracked editingItem!: FilterItem; // copy of the original. null if creating new
	@tracked private editingItemOriginal: FilterItem | null = null; // the original (from current metricSetup). null if creating new

	logicalOperatorsOptions: LabelValuePairString[] = [
		{ value: FilterLogicalOperator.AND, label: 'Match all (AND)' },
		{ value: FilterLogicalOperator.OR, label: 'Match any (OR)' },
	];

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.metricSetup = this.args.metricSetup.createCopy();

		const editItem = this.args.editItem;
		if (editItem) {
			// need to find the equivalent copy of the item that we will edit
			const editItemFromMetricSetup = this.metricSetup.dimensionFilterGroup?.filterItems.find(
				(i) => i.isEqual(editItem),
			);
			if (editItemFromMetricSetup) {
				this.setEditingItem(editItemFromMetricSetup);
			} else {
				// maybe the filter wasn't finalized yet (from master filter context popup)
				this.setEditingItem();
				this.editingItem.setFromFilterItemData(editItem.filterItemData);
			}
		} else {
			this.setEditingItem();
		}
	}

	get isMasterFilter() {
		return this.metricSetup instanceof MetricSetupForMasterFilter;
	}

	get isCollapsed() {
		return this.args.isInMasterContext;
	}

	get filterGroup(): FilterGroup | undefined {
		return this.metricSetup.dimensionFilterGroup;
	}

	get logicalOperator() {
		return this.filterGroup?.logicalOperator || FilterLogicalOperator.AND;
	}

	get isLogicalOperatorDisabled(): boolean {
		return !this.filterGroup;
	}

	get showClear() {
		return this.filterGroup && this.filterGroup?.filterItems.length > 0;
	}

	get showCancel() {
		return !!this.args.close;
	}

	get isEdit() {
		return !!this.editingItemOriginal;
	}

	get isEditingItemFilled() {
		return this.editingItem.values.length > 0;
	}

	@action
	endCollapsedMasterFilterMode() {
		// if filter is already filled, we add it to the group (or update if editing)
		// and clear the edit form
		if (this.isEditingItemFilled) {
			this.saveEditedItem();
		}

		this.mmMetricsService.openFiltersPopup(
			{
				metricSetup: this.metricSetup,
				editItem: this.editingItem,
				allowMultipleDimensions: this.args.allowMultipleDimensions,
				apply: (
					dimensions: string[] | undefined,
					filters: DSSetup.MetricSetupFiltersData | null,
				) => {
					this.args.apply(dimensions, filters);
				},
			},
			this.args.theme,
		);
	}

	@action
	changeLogicalOperator(logicalOperator: AnySimpleValue): void {
		if (this.filterGroup) {
			this.filterGroup.logicalOperator = logicalOperator as FilterLogicalOperator;
		}
	}

	@action
	changeDimensions(dimensions: string[]): void {
		this.metricSetup.dimensions = dimensions;
	}

	@action
	editFilter(item: FilterItem): void {
		this.setEditingItem(item);
	}

	@action
	removeFilter(item: FilterItem): void {
		this.analyticsService.event(TrackingEvent.FILTER_DATA_REMOVED);
		if (item === this.editingItemOriginal) {
			// remove from the edit form, if it's there
			this.setEditingItem();
		}
		this.filterGroup?.removeFilterItem(item);
	}

	@action
	saveEditedItem(): void {
		const editedItem = this.editingItem;
		const original = this.editingItemOriginal;
		if (original) {
			original.setFromFilterItemData(editedItem.filterItemData);
		} else {
			this.analyticsService.event(TrackingEvent.FILTER_DATA_ADDED, {
				operator: editedItem.operatorText,
				datasources: this.metricSetup.sourceTypeKeys,
				collapsed: this.isCollapsed,
			});
			if (this.filterGroup) {
				if (this.filterGroup.isFull) {
					this.infolayerService.error('You have reached the maximum number of filters');
					return;
				} else {
					this.filterGroup.addFilterItem(editedItem);
				}
			} else {
				this.metricSetup.addFilterGroup({
					logicalOperator: this.logicalOperator,
					filterItems: [editedItem.filterItemData],
				});
			}
		}

		this.setEditingItem();
	}

	@action
	clear(): void {
		this.analyticsService.event(TrackingEvent.FILTER_DATA_REMOVED);
		this.metricSetup.setFiltersFromData(null);
		this.setEditingItem();
	}

	@action
	clearAndClose(): void {
		this.clear();

		// remove leave confirm handler from popup
		this.args.popup.offAll(PopupEvent.CLOSE_INTENT);

		if (this.args.clearAndClose) {
			// for master filter
			this.args.clearAndClose();
		} else {
			// for dataselect filter
			this.apply();
		}
	}

	@action
	apply(): void {
		// add the current edited filter to the group if it's filled
		if (this.isEditingItemFilled) {
			this.saveEditedItem();
		} else if (this.editingItemOriginal) {
			// editing an existing filter but all values were cleared -> remove it
			// so the emptied filter resolves to null filtersData on apply
			this.removeFilter(this.editingItemOriginal);
		}

		const dimensionToSave =
			this.isMasterFilter &&
			(this.metricSetup as MetricSetupForMasterFilter).isUnspecificDimensionSelected
				? undefined
				: this.metricSetup.dimensions;
		this.args.apply(dimensionToSave, this.metricSetup.filtersData);

		// remove leave confirm handler from popup
		// can trigger bcos apply does not work synchronously
		this.args.popup.offAll(PopupEvent.CLOSE_INTENT);

		this.close();
	}

	@action
	close(): void {
		this.args.close?.();
	}

	@action
	checkIsDirty() {
		const originalGroup = this.args.metricSetup.dimensionFilterGroup,
			editedGroup = this.metricSetup.dimensionFilterGroup;
		return (
			(!!originalGroup && !editedGroup) ||
			(!originalGroup && !!editedGroup) ||
			(!!originalGroup && !!editedGroup && !originalGroup.isEqual(editedGroup))
		);
	}

	private setEditingItem(original?: FilterItem) {
		const field = original
			? original.field
			: this.metricSetup.metricDimensions.length === 1
				? this.metricSetup.metricDimensions[0]!.key
				: '';
		const itemCopy = new FilterItem({
			type: FilterItemType.DIMENSION,
			field,
			operator: original?.operator || this.metricSetup.getFirstAvailableMetricOperator(),
			values: original?.values || [],
			vendorOperator: original?.vendorOperator,
			source: original?.source,
		});
		this.editingItem = itemCopy;
		this.editingItemOriginal = original || null;
	}
	<template>
		{{#if this.isMasterFilter}}
			<div class="row ms-filters-dimension">
				<label>Breakdown by</label>
				<FormSelectDimensionsComponent
					@metricSetup={{this.metricSetup}}
					@onChange={{this.changeDimensions}}
					@showFloatlabel={{false}}
					@isMultiple={{@allowMultipleDimensions}}
					@placeholder="Select dimension"
					@theme={{@theme}}
				/>
			</div>
		{{/if}}

		<div class="ms-filters-editor">

			<BlockFiltersEditItemComponent
				@model={{this.editingItem}}
				@isEdit={{this.isEdit}}
				@editedItem={{this.editingItemOriginal}}
				@metricSetup={{this.metricSetup}}
				@onSave={{this.saveEditedItem}}
				@addMore={{if this.isCollapsed this.endCollapsedMasterFilterMode}}
				@onContentChange={{@onContentChange}}
				@theme={{@theme}}
			/>

			{{#unless this.isCollapsed}}
				<div class="ms-filters-list">

					<FormRadiobuttonGroupComponent
						class="radiobutton-default"
						@name="logicalOperator"
						@selected={{this.logicalOperator}}
						@options={{this.logicalOperatorsOptions}}
						@isDisabled={{this.isLogicalOperatorDisabled}}
						@onChange={{this.changeLogicalOperator}}
					/>

					<div class="ms-filters-header">
						<label>{{this.metricSetup.dimensionFilterItemsCountText}}</label>
						{{#if this.showClear}}
							<a {{on "click" this.clear}}>Clear all</a>
						{{/if}}
					</div>

					<div class="ms-filters-items {{themeClasses @theme}}">
						{{#each this.filterGroup.filterItems as |item index|}}
							<BlockFiltersListItemComponent
								@model={{item}}
								@logicalOperatorText={{if
									index
									this.filterGroup.logicalOperatorText
								}}
								@metricDimensions={{this.metricSetup.metricDimensions}}
								@edit={{fn this.editFilter item}}
								@remove={{fn this.removeFilter item}}
							/>
						{{/each}}
					</div>

				</div>
			{{/unless}}
		</div>

		<div class="controls">
			{{#if this.showClear}}
				<a class="action-remove" {{on "click" this.clearAndClose}}>Remove</a>
			{{/if}}

			{{#if this.isCollapsed}}
				<SubmitButtonComponent @onTrigger={{this.apply}} @theme="blue" @text="Apply" />
			{{else}}
				<FormLeaveConfirmPopupComponent
					@popup={{@popup}}
					@save={{this.apply}}
					@checkIsDirty={{this.checkIsDirty}}
					@slideFromRight={{true}}
					as |controls|
				>
					<controls.save @text="Apply" @theme="blue" />
				</FormLeaveConfirmPopupComponent>
			{{/if}}

			{{#if this.showCancel}}
				<a class="cancel" {{on "click" this.close}}>Cancel</a>
			{{/if}}
		</div>
	</template>
}
